import { useLayoutEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { links } from '@/utils/link'

type MeasureOneDataSourceConnectedEvent = CustomEvent<{
  data: {
    datasource_id: string
    datasource_name: string
  }
}>

interface MeasureOneWidgetProps {
  jobId: string
  config: string
  onTokenExpired: () => void
  onAddDataSource: (dataSourceId: string, dataSourceName: string) => void
  onMeasureOneError: () => void
}

export function MeasureOneWidget({
  config,
  jobId,
  onTokenExpired,
  onAddDataSource,
  onMeasureOneError,
}: MeasureOneWidgetProps) {
  const m1WidgetRef = useRef<HTMLElement>(null)
  const navigate = useNavigate()

  useLayoutEffect(() => {
    const element = m1WidgetRef.current
    const shadow = element?.shadowRoot
    if (shadow) {
      const styleTag = document.createElement('style')
      styleTag.textContent = `
        .m1-theme {
          width: 100% !important;
          max-width: none !important;
        }
      `
      shadow.appendChild(styleTag)
    }

    if (!element) {
      return
    }

    const onExit = () =>
      void navigate(links.measureOneSelectExperienceType(jobId), {
        replace: true,
      })

    const onDataSourceConnected = (event: Event) => {
      const { datasource_id: dataSourceId, datasource_name: dataSourceName } = (
        event as MeasureOneDataSourceConnectedEvent
      ).detail.data

      onAddDataSource(dataSourceId, dataSourceName)
    }

    const onDataSourceNotSupported = async (event: Event) => {
      await navigate(links.measureOneSelectExperienceType(jobId))

      console.error(event)

      onMeasureOneError()
    }

    element.addEventListener('exitRequested', onExit)
    element.addEventListener('tokenExpired', onTokenExpired)
    element.addEventListener('datasourceConnected', onDataSourceConnected)
    element.addEventListener('datasourceNotSupported', onDataSourceNotSupported)

    return () => {
      element.removeEventListener('exitRequested', onExit)
      element.removeEventListener('tokenExpired', onTokenExpired)
      element.removeEventListener('datasourceConnected', onDataSourceConnected)
      element.removeEventListener(
        'datasourceNotSupported',
        onDataSourceNotSupported,
      )
    }
  }, [
    onAddDataSource,
    config,
    jobId,
    navigate,
    onMeasureOneError,
    onTokenExpired,
  ])

  return <m1-link ref={m1WidgetRef} config={config} />
}
